import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Handshake, 
  Star, 
  Globe,
  Award,
  ArrowRight,
  Users,
  Target,
  Zap,
  Crown,
  Shield,
  Heart
} from 'lucide-react';

const PartnersPage = () => {
  const partnerCategories = [
    {
      category: "Gastronomie & Hôtellerie",
      icon: Award,
      color: "from-amber-500 to-orange-500",
      partners: [
        {
          name: "Relais & Châteaux",
          logo: "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Réseau mondial de maisons d'exception",
          collaboration: "Accès privilégié aux établissements membres"
        },
        {
          name: "Michelin Guide",
          logo: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Guide de référence gastronomique",
          collaboration: "Réservations prioritaires restaurants étoilés"
        },
        {
          name: "Leading Hotels of the World",
          logo: "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Collection d'hôtels de luxe",
          collaboration: "Services exclusifs et privilèges VIP"
        }
      ]
    },
    {
      category: "Transport & Mobilité",
      icon: Zap,
      color: "from-blue-500 to-indigo-500",
      partners: [
        {
          name: "NetJets",
          logo: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Leader mondial de l'aviation privée",
          collaboration: "Accès privilégié à la flotte de jets privés"
        },
        {
          name: "Superyacht Network",
          logo: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Réseau international de superyachts",
          collaboration: "Location exclusive de yachts d'exception"
        },
        {
          name: "Luxury Car Collection",
          logo: "https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Flotte de véhicules de prestige",
          collaboration: "Mise à disposition de chauffeurs experts"
        }
      ]
    },
    {
      category: "Art & Culture",
      icon: Crown,
      color: "from-purple-500 to-pink-500",
      partners: [
        {
          name: "Musées Privés Exclusifs",
          logo: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Accès aux collections privées",
          collaboration: "Visites privées et rencontres avec conservateurs"
        },
        {
          name: "Galeries d'Art Internationales",
          logo: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a04?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Réseau de galeries prestigieuses",
          collaboration: "Vernissages privés et acquisitions exclusives"
        },
        {
          name: "Maisons d'Enchères",
          logo: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Christie's, Sotheby's, Artcurial",
          collaboration: "Accès privilégié aux ventes exceptionnelles"
        }
      ]
    },
    {
      category: "Bien-être & Lifestyle",
      icon: Heart,
      color: "from-green-500 to-teal-500",
      partners: [
        {
          name: "Spas de Renommée Mondiale",
          logo: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Réseau international de spas luxury",
          collaboration: "Thérapeutes certifiés déplacés à domicile"
        },
        {
          name: "Personal Trainers Elite",
          logo: "https://images.unsplash.com/photo-1571019613540-996a8bebb1c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Coachs sportifs des célébrités",
          collaboration: "Sessions personnalisées dans votre villa"
        },
        {
          name: "Nutritionnistes Experts",
          logo: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
          description: "Spécialistes nutrition de pointe",
          collaboration: "Programmes alimentaires sur-mesure"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: Target,
      title: "Accès Exclusif",
      description: "Des privilèges impossibles à obtenir ailleurs"
    },
    {
      icon: Users,
      title: "Réseau Premium",
      description: "Plus de 500 partenaires dans le monde"
    },
    {
      icon: Shield,
      title: "Qualité Garantie",
      description: "Standards d'excellence certifiés"
    },
    {
      icon: Zap,
      title: "Service Immédiat",
      description: "Réactivité et disponibilité 24/7"
    }
  ];

  const partnershipTypes = [
    {
      title: "Partenaire Stratégique",
      description: "Intégration complète de nos services avec accès privilégié mutuel",
      benefits: ["Co-création d'expériences", "Accès aux clientèles premium", "Marketing conjoint"],
      commitment: "Partenariat à long terme avec investissement mutuel"
    },
    {
      title: "Fournisseur Premium",
      description: "Prestataires sélectionnés pour leur excellence et leur fiabilité",
      benefits: ["Volume d'affaires garanti", "Visibilité auprès de notre clientèle", "Formation continue"],
      commitment: "Respect strict de nos standards de qualité"
    },
    {
      title: "Partenaire Distributeur",
      description: "Réseau de distribution internationale de nos services",
      benefits: ["Exclusivité territoriale", "Support marketing", "Formation commerciale"],
      commitment: "Objectifs de développement ambitieux"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <Handshake className="w-4 h-4 mr-2" />
            Réseau d'Excellence
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Nos Partenaires
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Un écosystème d'excellence construit avec les meilleurs acteurs du luxe mondial 
            pour offrir des expériences impossibles ailleurs
          </p>

          <Button 
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Devenir Partenaire
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Pourquoi nos Partenariats Fonctionnent
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  <h3 className="text-lg font-medium text-gray-800 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Categories */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Notre Écosystème de Partenaires
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des alliances stratégiques avec les leaders de chaque secteur
            </p>
          </div>

          <div className="space-y-16">
            {partnerCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div key={index}>
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 bg-gradient-to-r ${category.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 
                      className="text-2xl font-light text-gray-800"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {category.category}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {category.partners.map((partner, idx) => (
                      <Card key={idx} className="p-6 hover:shadow-lg transition-all duration-300">
                        <div className="flex items-center gap-4 mb-4">
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div>
                            <h4 className="text-lg font-medium text-gray-800">{partner.name}</h4>
                            <p className="text-sm text-gray-600">{partner.description}</p>
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-700 font-medium mb-2">Notre collaboration :</p>
                          <p className="text-xs text-gray-600">{partner.collaboration}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Types de Partenariats
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Différents niveaux d'engagement pour différents objectifs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnershipTypes.map((type, index) => (
              <Card key={index} className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-white">
                <h3 
                  className="text-xl font-light mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {type.title}
                </h3>
                
                <p className="text-blue-200 text-sm mb-6 leading-relaxed">
                  {type.description}
                </p>
                
                <div className="space-y-3 mb-6">
                  <p className="text-sm font-medium text-blue-300">Avantages :</p>
                  {type.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Star className="w-3 h-3 text-blue-400" />
                      <span className="text-xs text-blue-100">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-white/20 pt-4">
                  <p className="text-xs text-blue-200">
                    <strong>Engagement :</strong> {type.commitment}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Become Partner CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Rejoignez l'Excellence PRIVIA
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Vous représentez l'excellence dans votre domaine ? 
            Construisons ensemble des expériences inoubliables pour une clientèle d'exception.
          </p>
          
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-light text-blue-600 mb-2">500+</div>
                <p className="text-sm text-gray-600 uppercase tracking-wide">Partenaires Actifs</p>
              </div>
              <div>
                <div className="text-3xl font-light text-blue-600 mb-2">50+</div>
                <p className="text-sm text-gray-600 uppercase tracking-wide">Pays Couverts</p>
              </div>
              <div>
                <div className="text-3xl font-light text-blue-600 mb-2">98%</div>
                <p className="text-sm text-gray-600 uppercase tracking-wide">Satisfaction Partenaires</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
              >
                Candidater comme Partenaire
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
              >
                Guide Partenaires
                <Globe className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Partners */}
      <section className="py-16 px-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h3 
            className="text-2xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Contact Partenariats
          </h3>
          
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <p className="text-sm text-gray-600 mb-2">Responsable Partenariats</p>
            <p className="text-lg font-medium text-gray-800 mb-2">Sarah Dubois</p>
            <p className="text-blue-600 mb-1">partnerships@priviacollection.com</p>
            <p className="text-blue-600">+1 786 840 0745</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;